/**
 * Textos do Relatório — os mesmos na tela e no arquivo exportado, para o
 * PDF dizer exatamente o que o cartão dizia. Puro.
 */
import { plural } from "../../utils/dinheiro";
import { FASES, SITUACOES } from "./dominio";

const dois = (n) => String(n).padStart(2, "0");

/** 09/10/26 — compacto, com ano (a planilha mistura 2025, 2026 e 2027). */
export function dataCurta(data) {
  if (!data) return "";
  return `${dois(data.getDate())}/${dois(data.getMonth() + 1)}/${dois(data.getFullYear() % 100)}`;
}

/** Faixa {de, ate, semData} → "09/10/26", "01/10–05/10/26" ou "" (sem nenhuma data). */
export function faixaCurta(faixa) {
  if (!faixa?.de) return "";
  if (faixa.de.getTime() === faixa.ate.getTime()) return dataCurta(faixa.de);
  const mesmoAno = faixa.de.getFullYear() === faixa.ate.getFullYear();
  const inicio = mesmoAno ? `${dois(faixa.de.getDate())}/${dois(faixa.de.getMonth() + 1)}` : dataCurta(faixa.de);
  return `${inicio}–${dataCurta(faixa.ate)}`;
}

/** "09/10/26 (3 s/ data)" · "a definir" quando nenhum ambiente tem a data. */
export function faixaComPendencia(faixa, total) {
  const texto = faixaCurta(faixa);
  if (!texto) return "a definir";
  return faixa.semData > 0 && faixa.semData < total ? `${texto} (${faixa.semData} s/ data)` : texto;
}

export function tomDaSituacao(situacaoId) {
  return SITUACOES.find((s) => s.id === situacaoId)?.tom || "neutral";
}

/** Pill do ambiente: "12 d de atraso", "vence hoje", "vence em 3 d", "no prazo", "sem prazo". */
export function textoSituacaoAmbiente(ambiente) {
  if (ambiente.situacao === "atrasado") return `${ambiente.diasAtraso} d de atraso`;
  if (ambiente.situacao === "em_breve") {
    return ambiente.diasParaPrazo === 0 ? "vence hoje" : `vence em ${ambiente.diasParaPrazo} d`;
  }
  return ambiente.situacao === "no_prazo" ? "no prazo" : "sem prazo";
}

/** "limite 20/08/26" (prazo próprio da etapa) ou "entrega 09/10/26" (etapa sem limite na planilha). */
export function textoPrazoAmbiente(ambiente) {
  if (!ambiente.prazo) return "";
  return `${ambiente.prazoOrigem === "entrega" ? "entrega" : "limite"} ${dataCurta(ambiente.prazo)}`;
}

/** Pill do cartão: o pior estado do grupo. */
export function situacaoDoGrupo(resumo) {
  if (resumo.atrasados > 0) {
    return { tom: "danger", texto: plural(resumo.atrasados, "atrasado", "atrasados") };
  }
  if (resumo.emBreve > 0) {
    return { tom: "warning", texto: `${resumo.emBreve} vence${resumo.emBreve > 1 ? "m" : ""} em breve` };
  }
  if (resumo.noPrazo > 0) return { tom: "success", texto: "no prazo" };
  return { tom: "neutral", texto: "sem prazo" };
}

/**
 * Estado do grupo para a lista: UM sinal de cor (o `tom` do ponto) e duas
 * linhas de texto neutro. `secao` diz em que bloco da lista o grupo entra.
 */
export function estadoDoGrupo(resumo) {
  const bloqueados = resumo.bloqueados > 0 ? plural(resumo.bloqueados, "bloqueado", "bloqueados") : "";
  const juntar = (...partes) => partes.filter(Boolean).join(" · ");
  if (resumo.atrasados > 0) {
    return {
      secao: "atrasado",
      tom: "danger",
      titulo: `${resumo.atrasados} de ${resumo.total} ${resumo.atrasados === 1 ? "atrasado" : "atrasados"}`,
      detalhe: juntar(`maior atraso ${plural(resumo.maiorAtraso, "dia", "dias")}`, bloqueados),
    };
  }
  if (resumo.emBreve > 0) {
    return {
      secao: "em_breve",
      tom: "warning",
      titulo: `${resumo.emBreve} de ${resumo.total} ${resumo.emBreve === 1 ? "vence" : "vencem"} em 7 dias`,
      detalhe: juntar(resumo.proximoPrazo ? `próximo prazo ${dataCurta(resumo.proximoPrazo)}` : "", bloqueados),
    };
  }
  return {
    secao: "em_dia",
    tom: null,
    titulo: resumo.noPrazo > 0 ? "No prazo" : "Sem prazo definido",
    detalhe: juntar(resumo.proximoPrazo ? `próximo prazo ${dataCurta(resumo.proximoPrazo)}` : "", bloqueados),
  };
}

/** Fases que têm ambiente, na ordem do fluxo: `[{id, label, total}]`. Zero não entra. */
export function fasesComAmbiente(resumo) {
  return FASES.map((fase) => ({ id: fase.id, label: fase.nome, total: resumo.porFase[fase.id].total })).filter(
    (fase) => fase.total > 0
  );
}

/** Blocos da lista quando a ordem é "mais crítico": o título diz o estado uma vez só. */
export const SECOES = [
  { id: "atrasado", titulo: "Com atraso" },
  { id: "em_breve", titulo: "Vencem em 7 dias" },
  { id: "em_dia", titulo: "Em dia" },
];

/** "7 de 10 atrasados · maior atraso 26 dias · 2 bloqueados" — vazio quando não há o que dizer. */
export function linhaDeAtraso(resumo) {
  const partes = [];
  if (resumo.atrasados > 0) {
    partes.push(`${resumo.atrasados} de ${resumo.total} ${resumo.atrasados === 1 ? "atrasado" : "atrasados"}`);
    partes.push(`maior atraso ${plural(resumo.maiorAtraso, "dia", "dias")}`);
  }
  if (resumo.bloqueados > 0) partes.push(plural(resumo.bloqueados, "bloqueado", "bloqueados"));
  return partes.join(" · ");
}

export function selosDoAmbiente(ambiente) {
  const selos = [];
  if (ambiente.bloqueado) {
    selos.push({ id: "bloqueado", tom: "danger", texto: `Bloqueado ${plural(ambiente.diasBloqueio, "dia", "dias")}` });
  }
  if (ambiente.terceirosPendente) selos.push({ id: "terceiros", tom: "warning", texto: "Terceiros" });
  if (ambiente.pinturaPendente) selos.push({ id: "pintura", tom: "warning", texto: "Pintura" });
  if (ambiente.obraFutura) selos.push({ id: "futura", tom: "neutral", texto: "Obra futura" });
  return selos;
}

// ---------------------------------------------------------------------------
// Exportação
// ---------------------------------------------------------------------------

function nomesAtrasados(grupo) {
  return grupo.ambientes
    .filter((a) => a.situacao === "atrasado")
    .map((a) => a.ambiente)
    .join(", ");
}

/**
 * `{ resumo, secoes }` no formato do BotaoExportacao. Duas seções:
 * o panorama (uma linha por cliente/pedido) e os ambientes (uma linha por
 * ambiente — é a que o Excel/CSV usa para filtrar e somar). Sai TUDO o que
 * está filtrado, inclusive ambientes de cartões recolhidos na tela.
 *
 * `comValores`: segue o "olho" de preços da tela. Sem ele as colunas de
 * valor nem entram no payload — o arquivo não carrega o que a tela escondia.
 */
export function montarExportacao({ grupos, resumoGeral, agrupamento, comValores }) {
  const rotuloGrupo = agrupamento === "pedido" ? "Pedido" : "Cliente";
  const moeda = (coluna) => (comValores ? [coluna] : []);

  const resumo = [
    { label: "Ambientes em aberto", value: resumoGeral.total, type: "number" },
    { label: "Atrasados", value: resumoGeral.atrasados, type: "number" },
    { label: "Vencem em 7 dias", value: resumoGeral.emBreve, type: "number" },
    { label: "No prazo", value: resumoGeral.noPrazo, type: "number" },
    { label: "Sem prazo", value: resumoGeral.semPrazo, type: "number" },
    { label: "Bloqueados", value: resumoGeral.bloqueados, type: "number" },
    ...(comValores ? [{ label: "Valor em aberto", value: resumoGeral.valor, type: "currency" }] : []),
  ];

  const colunasPanorama = [
    { key: "grupo", label: rotuloGrupo, type: "text" },
    { key: "complemento", label: agrupamento === "pedido" ? "Cliente" : "Pedidos", type: "text" },
    { key: "situacao", label: "Situação", type: "status" },
    { key: "ambientes", label: "Ambientes", type: "number" },
    { key: "atrasados", label: "Atrasados", type: "number" },
    { key: "maiorAtraso", label: "Maior atraso (dias)", type: "number" },
    ...FASES.map((fase) => ({ key: `fase_${fase.id}`, label: fase.label, type: "number" })),
    { key: "venda", label: "Venda", type: "text" },
    { key: "contrato", label: "Entrega do contrato", type: "text" },
    { key: "carregamento", label: "Carregamento previsto", type: "text" },
    { key: "ambientesAtrasados", label: "Ambientes atrasados", type: "text" },
    ...moeda({ key: "valor", label: "Valor em aberto", type: "currency" }),
  ];

  const linhasPanorama = grupos.map((grupo) => ({
    grupo: grupo.titulo,
    complemento: grupo.subtitulo,
    situacao: situacaoDoGrupo(grupo.resumo).texto,
    ambientes: grupo.resumo.total,
    atrasados: grupo.resumo.atrasados,
    maiorAtraso: grupo.resumo.maiorAtraso,
    ...Object.fromEntries(FASES.map((fase) => [`fase_${fase.id}`, grupo.resumo.porFase[fase.id].total])),
    venda: faixaCurta(grupo.venda),
    contrato: faixaCurta(grupo.entregaContrato),
    carregamento: faixaComPendencia(grupo.carregamentoPrevisto, grupo.resumo.total),
    ambientesAtrasados: nomesAtrasados(grupo),
    valor: grupo.resumo.valor,
  }));

  const colunasAmbientes = [
    { key: "cliente", label: "Cliente", type: "text" },
    { key: "pedido", label: "Pedido", type: "text" },
    { key: "ambiente", label: "Ambiente", type: "text" },
    { key: "lote", label: "Lote", type: "text" },
    { key: "etapa", label: "Etapa atual", type: "text" },
    { key: "situacao", label: "Situação", type: "status" },
    { key: "prazo", label: "Prazo da etapa", type: "date" },
    { key: "diasAtraso", label: "Dias de atraso", type: "number" },
    { key: "pendencias", label: "Pendências", type: "text" },
    { key: "venda", label: "Venda", type: "date" },
    { key: "contrato", label: "Entrega do contrato", type: "date" },
    { key: "carregamento", label: "Carregamento previsto", type: "date" },
    { key: "vendedor", label: "Vendedor", type: "text" },
    { key: "consultor", label: "Consultor", type: "text" },
    { key: "marceneiro", label: "Marceneiro", type: "text" },
    { key: "projetista", label: "Projetista", type: "text" },
    ...moeda({ key: "valor", label: "Valor", type: "currency" }),
  ];

  const linhasAmbientes = grupos.flatMap((grupo) =>
    grupo.ambientes.map((ambiente) => ({
      cliente: ambiente.cliente,
      pedido: ambiente.pedido,
      ambiente: ambiente.ambiente,
      lote: ambiente.lote,
      etapa: ambiente.etapaLabel,
      situacao: textoSituacaoAmbiente(ambiente),
      prazo: ambiente.prazo,
      diasAtraso: ambiente.diasAtraso,
      pendencias: selosDoAmbiente(ambiente)
        .map((selo) => selo.texto)
        .join(", "),
      venda: ambiente.venda,
      contrato: ambiente.entregaContrato,
      carregamento: ambiente.carregamentoPrevisto,
      vendedor: ambiente.vendedor,
      consultor: ambiente.consultor,
      marceneiro: ambiente.marceneiro,
      projetista: ambiente.projetista,
      valor: ambiente.valor,
    }))
  );

  return {
    resumo,
    secoes: [
      {
        id: "panorama",
        label: `Panorama por ${rotuloGrupo.toLowerCase()}`,
        columns: colunasPanorama,
        rows: linhasPanorama,
      },
      { id: "ambientes", label: "Ambientes em aberto", columns: colunasAmbientes, rows: linhasAmbientes },
    ],
  };
}
